/**
 * AIMDv2 – ToneDropdown
 *
 * Custom multi-select dropdown for Program Tone.
 * Each option has a title + description, and a checkbox indicator.
 * Unlike the generic DosenDropdown, this component renders a two-line
 * layout per menu item to match the Figma spec.
 *
 * Max selection count is enforced via the MAX_TONE_SELECTIONS constant.
 */

import React, { useRef, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Popper,
  Typography
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ToneOption } from '../types';
import { MAX_TONE_SELECTIONS } from '../constants';

interface ToneDropdownProps {
  label?: string;
  options: ToneOption[];
  value: string[];
  onChange: (value: string[]) => void;
  maxSelections?: number;
  disabled?: boolean;
  error?: string;
}

const ToneDropdown: React.FC<ToneDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  maxSelections = MAX_TONE_SELECTIONS,
  disabled = false,
  error
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  const handleSelect = (optionValue: string) => {
    const isSelected = value.includes(optionValue);
    if (isSelected) {
      onChange(value.filter((v) => v !== optionValue));
    } else if (value.length < maxSelections) {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FDFDFD)',
            lineHeight: '20px'
          }}
        >
          {label}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          width: '100%',
          px: '12px',
          py: '8px',
          background: 'rgba(20,20,20,1)',
          borderRadius: '6px',
          outline: open
            ? '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
            : '1px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
          display: 'flex',
          alignItems: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          minHeight: 40
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {value.length === 0 && (
            <Typography
              sx={{
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 14,
                fontFamily: 'Inter'
              }}
            >
              Select tones...
            </Typography>
          )}
          {value.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <Chip
                key={v}
                label={opt?.label ?? v}
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={() => handleRemove(v)}
                deleteIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                sx={{
                  height: 24,
                  borderRadius: '20px',
                  background: 'var(--Fill-Button-button-white, #FDFDFD)',
                  color: 'var(--Stroke-solid-black, #0F0F0F)',
                  fontSize: 12,
                  fontFamily: 'Inter',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-deleteIcon': {
                    color: 'var(--Stroke-solid-black, #0F0F0F)',
                    fontSize: 12
                  }
                }}
              />
            );
          })}
        </Box>

        {open ? (
          <KeyboardArrowUpIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        )}
      </Box>

      {/* Dropdown panel */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              mt: '4px',
              background: 'rgba(20,20,20,1)',
              borderRadius: '6px',
              outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
              overflow: 'hidden',
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            {options.map((opt) => {
              const isSelected = value.includes(opt.value);
              const isDisabledItem = !isSelected && value.length >= maxSelections;

              return (
                <Box
                  key={opt.value}
                  onClick={() => !isDisabledItem && handleSelect(opt.value)}
                  sx={{
                    px: '12px',
                    py: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: isDisabledItem ? 'not-allowed' : 'pointer',
                    opacity: isDisabledItem ? 0.4 : 1,
                    '&:hover': {
                      background: isDisabledItem ? 'transparent' : 'rgba(255,255,255,0.06)'
                    }
                  }}
                >
                  {/* Row with checkbox + label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {isSelected ? (
                      <CheckBoxIcon
                        sx={{
                          fontSize: 20,
                          color: 'var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.7))'
                        }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          fontSize: 20,
                          color: 'var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.5))'
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontFamily: 'Inter',
                        lineHeight: '20px',
                        color: isSelected
                          ? 'var(--Text-text-primary, #FDFDFD)'
                          : 'var(--Text-text-secondary, #B6B6B6)'
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Box sx={{ pl: '30px' }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'Inter',
                        lineHeight: '16px',
                        color: isSelected
                          ? 'var(--Text-text-secondary, #B6B6B6)'
                          : 'var(--Text-text-tertiary, #868686)'
                      }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>

      {error && (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: 'var(--UI-Error, #F44336)',
            lineHeight: '16px'
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ToneDropdown;
